import { useEffect, useMemo, useState } from "react";
import { Box, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { presentationApi } from "api/immunization";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import { validateCode, validateRequired } from "./common/validation";

const VaccinePresentationModal = ({ open, onClose, data }) => {
  const recordId = data?._id;
  const isUpdate = Boolean(recordId);

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      display: data?.display || "",
      description: data?.description || "",
      isMultiDose: Boolean(data?.isMultiDose),
      requiresReconstitution: Boolean(data?.requiresReconstitution),
      defaultDosesPerVial: data?.defaultDosesPerVial ?? 1,
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const validationError = useMemo(() => {
    const doses = Number(form.defaultDosesPerVial);

    return (
      validateCode(form.code, { isUpdate, label: "Presentation code" }) ||
      validateRequired(form.display, "Display name") ||
      (form.isMultiDose && doses < 2 ?
        "A multi-dose presentation must hold at least two doses per vial"
      : "") ||
      (!form.isMultiDose && doses !== 1 ?
        "A single-dose presentation must hold exactly one dose"
      : "")
    );
  }, [form, isUpdate]);

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      display: form.display.trim(),
      description: form.description?.trim() || "",
      isMultiDose: form.isMultiDose,
      requiresReconstitution: form.requiresReconstitution,
      defaultDosesPerVial: Number(form.defaultDosesPerVial) || 1,
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await presentationApi.update(recordId, payload);
        toast.success("Vaccine presentation updated successfully");
      } else {
        await presentationApi.create({ ...payload, code: form.code.trim() });
        toast.success("Vaccine presentation created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} vaccine presentation`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Vaccine Presentation"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <CodeField
          value={form.code}
          onChange={(value) => set({ code: value })}
          isUpdate={isUpdate}
          label="Presentation Code"
          helperText='e.g. "prefilled syringe" becomes PREFILLED_SYRINGE'
        />
        <TextField
          label="Display Name"
          required
          fullWidth
          size="small"
          value={form.display || ""}
          onChange={(event) => set({ display: event.target.value })}
        />
      </Box>

      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.description || ""}
        onChange={(event) => set({ description: event.target.value })}
      />

      <Box
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <TextField
          label="Doses Per Vial"
          type="number"
          size="small"
          sx={{ minWidth: 160 }}
          value={form.defaultDosesPerVial}
          onChange={(event) => set({ defaultDosesPerVial: event.target.value })}
        />
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.isMultiDose)}
              onChange={(event) => {
                const checked = event.target.checked;
                set({
                  isMultiDose: checked,
                  defaultDosesPerVial:
                    checked ?
                      Math.max(Number(form.defaultDosesPerVial) || 0, 2)
                    : 1,
                });
              }}
            />
          }
          label="Multi dose"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.requiresReconstitution)}
              onChange={(event) =>
                set({ requiresReconstitution: event.target.checked })
              }
            />
          }
          label="Requires reconstitution"
        />
      </Box>
    </MasterModalShell>
  );
};

export default VaccinePresentationModal;
